import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Building2, Check, ChevronDown, Info, LogOut, MapPin, ShieldCheck, UserRound } from 'lucide-react';
import { callRpc } from '@/api/resources';
import { Drawer, Field } from '@/components/live';
import { useAuth } from '@/auth/AuthProvider';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';
import { forgetReason, registerReasonPrompt } from '@/lib/changeReason';
import { guideFor } from '@/lib/pageGuides';

interface OrgRow { organization_id: string; code: string; name: string; is_home: boolean; is_current: boolean; expires_at: string | null }

export function useMyOrganizations() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: ['my-organizations'],
    queryFn: () => callRpc<OrgRow[]>('fn_my_organizations'),
    enabled: isSupabaseConfigured && Boolean(profile),
  });
}

/** Close a popover on an outside click or Escape. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => { if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false); };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return { open, setOpen, ref };
}

/** Switch into an organisation: no reason asked (owner, 2026-09-22), the switch is audited. */
function useSwitchOrganization() {
  const { refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  return async (row: OrgRow) => {
    if (row.is_home) await callRpc('fn_leave_organization');
    else await callRpc('fn_enter_organization', { p_organization_id: row.organization_id });
    forgetReason();
    queryClient.clear();                    // everything cached belongs to the organisation left
    await refreshProfile();
    toast.success(row.is_home ? 'Back in your own organisation.' : `Now working in ${row.name}.`);
  };
}

/**
 * The organisation picker, in the header (owner, 2026-09-22: "As app admin - once i login ask me
 * to pick the org ... provide in header", "dialogue box for switching - confirm switch", "remove
 * this org switch from leftside menu"). Shown only to someone with more than one organisation.
 */
export function HeaderOrgPicker() {
  const orgs = useMyOrganizations();
  const rows = orgs.data ?? [];
  const current = rows.find((row) => row.is_current);
  const { open, setOpen, ref } = usePopover();
  const [confirming, setConfirming] = useState<OrgRow | null>(null);
  const [busy, setBusy] = useState(false);
  const switchTo = useSwitchOrganization();
  if (rows.length <= 1) return null;

  async function confirm() {
    if (!confirming) return;
    setBusy(true);
    try {
      await switchTo(confirming);
      setConfirming(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That organisation could not be opened.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="header-pop" ref={ref}>
      <button aria-expanded={open} className={`header-org${current && !current.is_home ? ' is-away' : ''}`} onClick={() => setOpen(!open)}
              title="Switch organisation" type="button">
        <Building2 size={16} />
        <span className="header-org-name">{current?.name ?? 'Organisation'}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="header-menu" role="menu">
          <div className="header-menu-title">Organisation</div>
          {rows.map((row) => (
            <button className={row.is_current ? 'active' : undefined} disabled={row.is_current} key={row.organization_id}
                    onClick={() => { setOpen(false); setConfirming(row); }} role="menuitem" type="button">
              {row.is_current ? <Check size={14} /> : <Building2 size={14} />}
              <span>{row.name}{row.is_home && <em> (yours)</em>}</span>
            </button>
          ))}
        </div>
      )}
      <ConfirmSwitch busy={busy} onCancel={() => setConfirming(null)} onConfirm={() => void confirm()} target={confirming} />
    </div>
  );
}

function ConfirmSwitch({ target, busy, onCancel, onConfirm }: { target: OrgRow | null; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  return (
    <Drawer
      footer={<><button className="cancel" onClick={onCancel} type="button">Cancel</button>
        <button className="primary" disabled={busy} onClick={onConfirm} type="button">{busy ? 'Switching...' : 'Switch'}</button></>}
      onClose={onCancel} open={Boolean(target)} title={`Switch to ${target?.name ?? ''}?`}
    >
      <p style={{ margin: 0 }}>
        {target?.is_home
          ? 'You will be back in your own organisation.'
          : <>You will work inside <strong>{target?.name}</strong>. The switch is recorded in their audit trail, and each record you create, change or delete there asks for a reason.</>}
      </p>
    </Drawer>
  );
}

/** Straight after sign-in, an app admin with several organisations chooses where to work. */
export function OrgChooserOnLogin() {
  const orgs = useMyOrganizations();
  const rows = orgs.data ?? [];
  const [done, setDone] = useState(() => { try { return window.sessionStorage.getItem('vanam.org.chosen') === '1'; } catch { return false; } });
  const [busy, setBusy] = useState(false);
  const switchTo = useSwitchOrganization();
  if (done || rows.length <= 1) return null;
  const finish = () => { try { window.sessionStorage.setItem('vanam.org.chosen', '1'); } catch { /* per tab */ } setDone(true); };

  async function choose(row: OrgRow) {
    setBusy(true);
    try {
      if (!row.is_current) await switchTo(row);
      finish();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That organisation could not be opened.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer footer={<button onClick={finish} type="button">Stay in {rows.find((row) => row.is_current)?.name ?? 'the current one'}</button>}
            onClose={finish} open subtitle="You can switch again at any time from the header." title="Which organisation are you working in?">
      <div className="org-choice">
        {rows.map((row) => (
          <button className={row.is_current ? 'active' : undefined} disabled={busy} key={row.organization_id} onClick={() => void choose(row)} type="button">
            <Building2 size={18} />
            <span><strong>{row.name}</strong><small>{row.is_home ? 'Your own organisation' : row.code}{row.is_current ? ' · current' : ''}</small></span>
          </button>
        ))}
      </div>
    </Drawer>
  );
}

/** The user menu, top right: who is signed in, their role and area, and sign out. */
export function HeaderUserMenu() {
  const { profile, signOut } = useAuth();
  const { open, setOpen, ref } = usePopover();
  if (!profile) return null;
  const initials = profile.user.full_name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]!.toUpperCase()).join('');
  return (
    <div className="header-pop" ref={ref}>
      <button aria-expanded={open} aria-label="Your profile" className="header-avatar" onClick={() => setOpen(!open)} title={profile.user.full_name} type="button">
        {initials || <UserRound size={16} />}
      </button>
      {open && (
        <div className="header-menu header-profile" role="menu">
          <div className="header-profile-card">
            <span className="header-avatar large">{initials || <UserRound size={18} />}</span>
            <span><strong>{profile.user.full_name}</strong><small>{profile.organization?.name}</small></span>
          </div>
          <dl className="header-profile-facts">
            <div><dt><ShieldCheck size={13} /> Role</dt><dd>{profile.roles.map((role) => role.name).join(', ') || 'No role'}{profile.tier != null ? ` (tier ${profile.tier})` : ''}</dd></div>
            <div><dt><MapPin size={13} /> Area</dt><dd>{profile.geography_scopes.length === 0 ? 'No place assigned'
              : profile.geography_scopes.map((scope) => `${scope.name}${scope.mode === 'include_children' ? ' and below' : ''}`).join(', ')}</dd></div>
          </dl>
          <button className="header-signout" onClick={() => void signOut()} role="menuitem" type="button"><LogOut size={15} /> Sign out</button>
        </div>
      )}
    </div>
  );
}

/**
 * The page's guide (SOP), behind an "i" beside search (owner, 2026-09-22).
 *
 * `asMenuItem` renders it as a labelled row instead of a bare icon, for the header's compact
 * menu. Same state, same drawer - only the trigger differs, because an icon with a tooltip is
 * useless inside a menu the reader already opened to read words.
 */
export function HeaderPageGuide({ asMenuItem = false }: { asMenuItem?: boolean } = {}) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const guide = guideFor(location.pathname.replace(/^\//, '').split('/')[0] ?? '');
  useEffect(() => { setOpen(false); }, [location.pathname]);
  if (!guide) return null;
  return (
    <>
      {asMenuItem ? (
        <button className="header-more-item" onClick={() => setOpen(true)} role="menuitem" type="button">
          <Info size={16} /> How this page works
        </button>
      ) : (
        <button aria-label="How this page works" className="mobile-header-menu header-icon" onClick={() => setOpen(true)} title="How this page works" type="button">
          <Info size={18} />
        </button>
      )}
      <Drawer onClose={() => setOpen(false)} open={open} subtitle={guide.purpose} title={guide.title}>
        <div className="page-guide">
          {guide.sections.map((section) => (
            <section key={section.heading}>
              <h3>{section.heading}</h3>
              {section.steps && <ol>{section.steps.map((step) => <li key={step}>{step}</li>)}</ol>}
              {section.points && <ul>{section.points.map((point) => <li key={point}>{point}</li>)}</ul>}
            </section>
          ))}
        </div>
      </Drawer>
    </>
  );
}

/** Asks for the reason an app admin changes records inside another organisation. */
export function ChangeReasonHost() {
  const [request, setRequest] = useState<{ message: string; resolve: (value: string | null) => void } | null>(null);
  const [text, setText] = useState('');
  useEffect(() => {
    registerReasonPrompt((message) => new Promise((resolve) => { setText(''); setRequest({ message, resolve }); }));
    return () => registerReasonPrompt(null);
  }, []);
  if (!request) return null;
  const finish = (value: string | null) => { request.resolve(value); setRequest(null); };
  return (
    <Drawer
      footer={<><button className="cancel" onClick={() => finish(null)} type="button">Cancel</button>
        <button className="primary" disabled={!text.trim()} onClick={() => finish(text)} type="button">Save with this reason</button></>}
      onClose={() => finish(null)} open subtitle={request.message} title="Reason for this change"
    >
      <Field hint="Recorded in the record's change history, which the organisation's own users can read." label="Reason" required>
        <textarea autoFocus onChange={(event) => setText(event.target.value)} placeholder="e.g. Correcting the plot area as requested in ticket VAN-114" rows={3} value={text} />
      </Field>
    </Drawer>
  );
}
